import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { MoreHorizontal, Sun, Moon, User, LogOut, Check } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useThemeStore } from '@/stores/theme';
import { useMe } from '@/features/session/api';
import { useSession } from '@/features/session/useSession';
import { useWorkspaceStore } from '@/stores/workspace';
import { signOut } from '@/features/auth/api';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NAV_OPS, NAV_MANAGE, filterForRole } from './nav-items';

// Mobile primary navigation. The desktop sidebar is hidden below `md`; this
// fixed bottom tab bar takes its place with the first four ops as tabs and a
// "More" sheet for everything else plus account actions. §mobile-web-direction.
export function MobileNav() {
  const { t } = useTranslation();
  const { membership } = useActiveMembership();
  const role = membership?.role;
  const [moreOpen, setMoreOpen] = useState(false);

  const ops = filterForRole(NAV_OPS, role);
  const primary = ops.slice(0, 4);
  const overflow = [...ops.slice(4), ...filterForRole(NAV_MANAGE, role)];

  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-border bg-surface pb-safe md:hidden">
        {primary.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              cn(
                'flex min-h-safe-tap flex-1 flex-col items-center justify-center gap-1 py-2 text-[10px] font-semibold tracking-wide text-muted',
                isActive && 'text-accent-ink',
              )
            }
          >
            {({ isActive }) => (
              <>
                <item.icon size={22} strokeWidth={isActive ? 2.2 : 1.8} />
                <span className="max-w-full truncate">{t(item.labelKey)}</span>
              </>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className="flex min-h-safe-tap flex-1 flex-col items-center justify-center gap-1 py-2 text-[10px] font-semibold tracking-wide text-muted"
        >
          <MoreHorizontal size={22} strokeWidth={1.8} />
          <span>{t('nav.more')}</span>
        </button>
      </nav>

      <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} overflow={overflow} />
    </>
  );
}

function MoreSheet({
  open,
  onOpenChange,
  overflow,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  overflow: { labelKey: string; to: string; icon: typeof User }[];
}) {
  const { t } = useTranslation();
  const { pref, setPref } = useThemeStore();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { session } = useSession();
  const { activeWorkspaceId, setActiveWorkspaceId } = useWorkspaceStore();
  const memberships = me?.memberships ?? [];

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <DialogPrimitive.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col border-t border-border bg-surface pb-safe">
          <div className="flex justify-center pb-1 pt-2">
            <span className="h-1 w-9 rounded-full bg-border" />
          </div>
          <DialogPrimitive.Title className="px-5 pb-2 font-display text-base font-bold text-ink">
            {t('nav.menu')}
          </DialogPrimitive.Title>

          <div className="min-h-0 flex-1 overflow-auto pb-2">
            {overflow.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => onOpenChange(false)}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-safe-tap items-center gap-3 px-5 py-3 text-[15px] font-medium text-ink',
                    isActive && 'text-accent-ink',
                  )
                }
              >
                <item.icon size={20} strokeWidth={1.8} className="flex-none" />
                {t(item.labelKey)}
              </NavLink>
            ))}

            {memberships.length > 1 ? (
              <div className="mt-1 border-t border-border pt-1">
                <p className="px-5 py-2 text-[10px] font-semibold uppercase tracking-[.14em] text-muted">
                  {t('topbar.selectWorkspace')}
                </p>
                {memberships.map((m) => (
                  <button
                    key={m.workspaceId}
                    type="button"
                    onClick={() => {
                      if (session?.user.id) setActiveWorkspaceId(session.user.id, m.workspaceId);
                      onOpenChange(false);
                    }}
                    className="flex min-h-safe-tap w-full items-center justify-between px-5 py-3 text-left text-[15px] font-medium text-ink"
                  >
                    {m.name}
                    {m.workspaceId === activeWorkspaceId ? <Check size={18} className="text-accent-ink" /> : null}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="mt-1 flex items-center justify-between border-t border-border px-5 py-3">
              <span className="text-[15px] font-medium text-ink">{t('appearance.title')}</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPref(pref === 'dark' ? 'light' : 'dark')}
                  className="grid h-11 w-11 place-items-center border border-border text-muted"
                  aria-label={t('topbar.toggleTheme')}
                >
                  {pref === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                </button>
                <LanguageSwitcher />
              </div>
            </div>

            <NavLink
              to="/settings"
              onClick={() => onOpenChange(false)}
              className="flex min-h-safe-tap items-center gap-3 border-t border-border px-5 py-3 text-[15px] font-medium text-ink"
            >
              <User size={20} strokeWidth={1.8} className="flex-none" />
              {t('topbar.profile')}
            </NavLink>
            <button
              type="button"
              onClick={async () => {
                onOpenChange(false);
                await signOut();
                navigate('/login', { replace: true });
              }}
              className="flex min-h-safe-tap w-full items-center gap-3 border-t border-border px-5 py-3 text-left text-[15px] font-medium text-danger"
            >
              <LogOut size={20} strokeWidth={1.8} className="flex-none" />
              {t('topbar.signOut')}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
